import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Zoom,
  Stack,
  Divider,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Avatar,
  CircularProgress,
  useTheme
} from '@mui/material';
import {
  Business as Building2,
  Close as X,
  Edit as Edit2,
  Delete as Trash2,
  LocationOn as MapPin,
  Phone,
  Store,
  AddCircle as PlusCircle,
  CloudUpload as Upload,
  Image as ImageIcon,
  Save,
  Visibility
} from '@mui/icons-material';

// Import custom components and services
import apiClient from '../../../../api/client';
import uploadService from '../../services/uploadService';
import AddNewBranch from './dialogs/AddNewBranch';
import EditBranch from './dialogs/EditBranch';
import DeleteDialog from './dialogs/DeleteDialog';
import SuccessDialog from './dialogs/SuccessDialog';

// Styled Components
const StyledContainer = styled(Box)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    background: isDarkMode ? '#121212' : '#f6f9fc',
    minHeight: '100vh',
    padding: theme.spacing(3),
    borderRadius: '16px',
    transition: 'all 0.3s ease',
    backdropFilter: 'blur(10px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    WebkitOverflowScrolling: 'touch',
    
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      paddingBottom: theme.spacing(10),
      height: 'calc(100vh - 120px)'
    }
  };
});

const StyledCard = styled(Card)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    background: isDarkMode ? '#1e1e1e' : '#ffffff',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    boxShadow: isDarkMode 
      ? '0 8px 32px rgba(0, 0, 0, 0.3)' 
      : '0 8px 32px rgba(0, 0, 0, 0.08)',
    border: isDarkMode 
      ? '1px solid rgba(255, 255, 255, 0.1)' 
      : '1px solid rgba(255, 255, 255, 0.4)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    height: '100%',
  };
});

const CardHeader = styled(Box)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    background: isDarkMode 
      ? 'linear-gradient(135deg, #4a5568 0%, #553c8b 100%)'
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: theme.spacing(3),
    borderRadius: '16px 16px 0 0',
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };
});

const StyledTableContainer = styled(TableContainer)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    maxHeight: '70vh',
    overflow: 'auto',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      width: '6px',
      height: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(102, 126, 234, 0.2)',
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(102, 126, 234, 0.2) transparent',
    '& .MuiTableCell-head': {
      backgroundColor: isDarkMode 
        ? 'rgba(102, 126, 234, 0.15)' 
        : 'rgba(102, 126, 234, 0.05)',
      color: isDarkMode ? '#ffffff' : '#333',
      fontWeight: 600,
      padding: theme.spacing(2),
      whiteSpace: 'nowrap',
    },
    '& .MuiTableRow-root': {
      '&:hover': {
        backgroundColor: isDarkMode 
          ? 'rgba(102, 126, 234, 0.1)' 
          : 'rgba(102, 126, 234, 0.05)',
      }
    },
    '& .MuiTableCell-body': {
      padding: theme.spacing(2),
      borderBottom: `1px solid ${isDarkMode ? '#404040' : 'rgba(102, 126, 234, 0.1)'}`,
      color: isDarkMode ? '#ffffff' : 'inherit',
    }
  };
});

const AnimatedButton = styled(Button)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    background: isDarkMode 
      ? 'linear-gradient(135deg, #4a5568 0%, #553c8b 100%)'
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    borderRadius: '12px',
    padding: '10px 20px',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
    '&:hover': {
      background: isDarkMode 
        ? 'linear-gradient(135deg, #3a4454 0%, #453177 100%)'
        : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(102, 126, 234, 0.3)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
      transition: 'all 0.5s ease',
    },
    '&:hover::before': {
      left: '100%',
    }
  };
});

// Action Buttons
const EditButton = styled(IconButton)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    color: '#667eea',
    backgroundColor: isDarkMode 
      ? 'rgba(102, 126, 234, 0.2)' 
      : 'rgba(102, 126, 234, 0.1)',
    transition: 'all 0.2s ease',
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: isDarkMode 
        ? 'rgba(102, 126, 234, 0.3)' 
        : 'rgba(102, 126, 234, 0.2)',
      transform: 'translateY(-2px)',
    }
  };
});

const DeleteButton = styled(IconButton)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    color: '#f43f5e',
    backgroundColor: isDarkMode 
      ? 'rgba(244, 63, 94, 0.2)' 
      : 'rgba(244, 63, 94, 0.1)',
    transition: 'all 0.2s ease',
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: isDarkMode 
        ? 'rgba(244, 63, 94, 0.3)' 
        : 'rgba(244, 63, 94, 0.2)',
      transform: 'translateY(-2px)',
    }
  };
});

const StyledTextField = styled(TextField)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      transition: 'all 0.2s ease',
      backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
      color: isDarkMode ? '#ffffff' : 'inherit',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#667eea',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#667eea',
        borderWidth: '2px',
      }
    },
    '& .MuiInputLabel-root': {
      color: isDarkMode ? '#b3b3b3' : 'inherit',
      '&.Mui-focused': {
        color: '#667eea',
      }
    },
    '& .MuiFormHelperText-root': {
      color: isDarkMode ? '#b3b3b3' : 'inherit',
    }
  };
});

const UploadBox = styled(Box)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    border: `2px dashed ${isDarkMode ? 'rgba(102, 126, 234, 0.7)' : 'rgba(102, 126, 234, 0.5)'}`,
    borderRadius: '12px',
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: isDarkMode 
      ? 'rgba(102, 126, 234, 0.1)' 
      : 'rgba(102, 126, 234, 0.05)',
    '&:hover': {
      backgroundColor: isDarkMode 
        ? 'rgba(102, 126, 234, 0.15)' 
        : 'rgba(102, 126, 234, 0.1)',
      borderColor: '#667eea',
    }
  };
});

const ImagePreview = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

// Main VendorSettings Component
const VendorSetting = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  // State for tabs
  const [currentTab, setCurrentTab] = useState(0);
  
  // State for general vendor information
  const [vendorInfo, setVendorInfo] = useState({
    name: '',
    logo: null,
    logoUrl: null,
  });
  
  // State for vendor information loading
  const [loadingVendorInfo, setLoadingVendorInfo] = useState(true);
  
  // State management for branches
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  
  // Dialog states
  const [addBranchDialogOpen, setAddBranchDialogOpen] = useState(false);
  const [editBranchDialogOpen, setEditBranchDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch vendor information
  const fetchVendorInfo = useCallback(async () => {
    setLoadingVendorInfo(true);
    setError(null); // Clear any previous errors
    
    try {
      // Check if user has a token first
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        setError(t('vendor.loadError'));
        return;
      }
      
      console.log('🔍 Fetching vendor details...');
      const response = await apiClient.get('/api/v1/users/vendor-details');
      
      console.log('✅ Vendor Info Response:', response.data);
      
      // Fix malformed image URL
      let logoUrl = null;
      
      if (response.data.image) {
        // Check if the image path contains an embedded URL
        if (response.data.image.includes('https://')) {
          // Extract the actual URL from the malformed path
          const urlMatch = response.data.image.match(/(https:\/\/.*\.jpg)/);
          if (urlMatch && urlMatch[1]) {
            logoUrl = urlMatch[1];
          }
        } else {
          // Use the original URL construction if it's just a filename
          logoUrl = `https://storage.googleapis.com/poslah-3c631.firebasestorage.app/vendor-logos/${response.data.image}`;
        }
      }
      
      console.log('Extracted Image URL:', logoUrl);
      
      // Test image loading
      if (logoUrl) {
        const img = new Image();
        img.onload = () => console.log('Image loaded successfully:', logoUrl);
        img.onerror = () => console.error('Image failed to load:', logoUrl);
        img.src = logoUrl;
      }
  
      setVendorInfo({
        name: response.data.vendor_name || '',
        logoUrl: logoUrl,
        contact_person: response.data.contact_person,
        contact_number: response.data.vendor_contact || response.data.contact_number // Fix field name mismatch
      });
      
      console.log('✅ Vendor info successfully loaded');
    
    } catch (error) {
      console.error('❌ Vendor Info Fetch Error:', error);
      
      // Enhanced error logging
      if (error.response) {
        console.error('API Error Response:', {
          status: error.response.status,
          message: error.response.data?.message,
          data: error.response.data
        });
        
        // Handle specific error cases
        if (error.response.status === 401) {
          console.error('Authentication failed - redirecting to login');
          localStorage.removeItem('token');
          localStorage.removeItem('userDetails');
          // The API client should handle the redirect, but let's ensure it
          if (!window.location.pathname.includes('/signin')) {
            window.location.href = '/signin';
          }
          return;
        }
      } else if (error.request) {
        console.error('Network Error:', error.request);
      } else {
        console.error('Error Message:', error.message);
      }
      
      setError(t('vendor.loadError'));
    } finally {
      setLoadingVendorInfo(false);
    }
  }, [t]);

  const handleViewLogo = () => {
    if (vendorInfo.logoUrl) {
      window.open(vendorInfo.logoUrl, '_blank');
    } else {
      setError(t('vendor.noLogoAvailable'));
    }
  };
  
  // Fetch branches
  const fetchBranches = useCallback(async () => {
    setLoadingBranches(true);
    try {
      console.log('🔍 Fetching branches...');
      const response = await apiClient.get('/api/v1/users/list-branches');
      console.log('✅ Branches Response:', response.data);
      setBranches(response.data);
    } catch (error) {
      console.error('❌ Error fetching branches:', error);
      
      // Enhanced error logging for branches
      if (error.response) {
        console.error('Branches API Error Response:', {
          status: error.response.status,
          message: error.response.data?.message,
          data: error.response.data
        });
      }
      
      setError(t('vendor.loadError'));
    } finally {
      setLoadingBranches(false);
    }
  }, [t]);
  
  // Initialize data
  useEffect(() => {
  fetchVendorInfo();
  fetchBranches();
}, [fetchVendorInfo, fetchBranches]);
  
  // Handle vendor logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    try {
      // Validate the image using uploadService
      const validation = uploadService.validateImage(file);
      if (!validation.isValid) {
        setError(validation.message);
        return;
      }
  
      // Convert file to base64 using vendor logo upload method
      const base64Image = await uploadService.uploadVendorLogo(file);
  
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      
      setVendorInfo(prev => ({
        ...prev,
        logo: base64Image,
        logoUrl: previewUrl
      }));
      
    } catch (error) {
      console.error('Error handling logo upload:', error);
      setError(error.message || t('vendor.logoProcessError'));
    }
  };
  
  const handleSaveVendorInfo = async () => {
    try {
      setLoading(true);
      
      // **FIXED: Send as JSON, not FormData**
      const requestData = {
        name: vendorInfo.name
      };
      
      // Add image data if logo was changed
      if (vendorInfo.logo) {
        requestData.image = vendorInfo.logo;
      }
      
      console.log('🚀 Sending vendor update request:', requestData);
      
      const response = await apiClient.put('/api/v1/vendors/update', requestData);
      
      if (response.status === 200) {
        console.log('✅ Vendor update response:', response.data);
        
        // **FIXED: Update local state with correct response structure**
        setVendorInfo(prev => ({
          ...prev,
          name: response.data.name || prev.name,               // Direct access
          logoUrl: response.data.image || prev.logoUrl,        // Direct access
          logo: null
        }));
        
        setSuccessMessage(t('vendor.updateSuccess'));
        setSuccessDialogOpen(true);

        // **FIXED: Dispatch event with correct data structure**
        window.dispatchEvent(new CustomEvent('vendorDataUpdated', {
          detail: {
            name: response.data.name || vendorInfo.name,
            logoUrl: response.data.image || vendorInfo.logoUrl
          }
        }));
        
        // Keep existing event for compatibility
        window.dispatchEvent(new CustomEvent('vendor-updated'));
        
        // Refresh vendor info to get updated data
        await fetchVendorInfo();
      }
    } catch (error) {
      console.error('❌ Error updating vendor:', error);
      console.error('Error details:', error.response?.data);
      setError(error.response?.data?.message || t('vendor.updateError'));
    } finally {
      setLoading(false);
    }
  };
  // Handle branch operations
  const handleAddBranch = () => {
    setAddBranchDialogOpen(true);
  };
  
  const handleEditBranch = (branch) => {
    setSelectedBranch(branch);
    setEditBranchDialogOpen(true);
  };
  
  const handleDeleteBranch = (branch) => {
    setSelectedBranch(branch);
    setDeleteDialogOpen(true);
  };
  
  const handleBranchAdded = (newBranch) => {
    console.log('🏗️ Adding new branch:', newBranch);
    
    // Ensure the new branch has all required fields
    const completeNewBranch = {
      branch_id: newBranch.branch_id || newBranch.id,
      location_name: newBranch.location_name || newBranch.name || '',
      address: newBranch.address || '',
      contact_number: newBranch.contact_number || '',
      created_at: newBranch.created_at || new Date().toISOString(),
      ...newBranch // Spread to include any other fields
    };
    
    setBranches(prevBranches => [...prevBranches, completeNewBranch]);
    setSuccessMessage(t('vendor.branchAdded'));
    setSuccessDialogOpen(true);
    setAddBranchDialogOpen(false);
    
    // Refresh the branch list to ensure consistency
    setTimeout(() => {
      fetchBranches();
    }, 500);
  };
  
  const handleBranchUpdated = (updatedBranch) => {
    setBranches(branches.map(branch => 
      branch.branch_id === updatedBranch.branch_id ? updatedBranch : branch
    ));
    setSuccessMessage(t('vendor.branchUpdated'));
    setSuccessDialogOpen(true);
    setEditBranchDialogOpen(false);
  };
  
  const handleBranchDeleted = async () => {
    try {
      await apiClient.delete(`/api/v1/users/delete-branch/${selectedBranch.branch_id}`);
      setBranches(branches.filter(branch => branch.branch_id !== selectedBranch.branch_id));
      setSuccessMessage(t('vendor.branchDeleted'));
      setSuccessDialogOpen(true);
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting branch:', error);
      setError(t('vendor.branchDeleteError'));
    }
  };
  
  // Clear error message
  const handleErrorClose = () => {
    setError(null);
  };
  
  return (
    <StyledContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 600,
            color: isDarkMode ? '#ffffff' : 'inherit'
          }}
        >
          {t('vendor.title')}
        </Typography>
      </Box>
      
      {/* Status Messages */}
      <Box sx={{ mb: 3 }}>
        {error && (
          <Zoom in={!!error}>
            <Box 
              sx={{ 
                p: 2, 
                mb: 2, 
                borderRadius: '10px',
                boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)',
                bgcolor: isDarkMode 
                  ? 'rgba(244, 67, 54, 0.2)' 
                  : 'rgba(244, 67, 54, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: isDarkMode ? '1px solid rgba(244, 67, 54, 0.3)' : undefined
              }}
            >
              <Typography 
                variant="body1" 
                color="error" 
                fontWeight={500}
                sx={{ color: isDarkMode ? '#ffffff' : 'error' }}
              >
                {error}
              </Typography>
              <IconButton size="small" onClick={handleErrorClose}>
                <X sx={{ color: isDarkMode ? '#ffffff' : 'inherit' }} />
              </IconButton>
            </Box>
          </Zoom>
        )}
      </Box>
      
      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: '#667eea',
            },
            '& .MuiTab-root': {
              color: isDarkMode ? '#b3b3b3' : 'inherit',
              '&.Mui-selected': {
                color: '#667eea',
              }
            }
          }}
        >
          <Tab label={t('vendor.vendorInfo')} />
          <Tab label={t('vendor.branchManagement')} />
        </Tabs>
      </Box>
      
      {/* Vendor Information Tab */}
      {currentTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StyledCard>
              <CardHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Building2 size={24} />
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                    {t('vendor.vendorInfo')}
                  </Typography>
                </Box>
              </CardHeader>
              
              <CardContent sx={{ p: 3 }}>
                {loadingVendorInfo ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={4}>
                    {/* Vendor Logo */}
                    <Grid item xs={12} md={6}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mb: 2, 
                          fontWeight: 600,
                          color: isDarkMode ? '#ffffff' : 'inherit'
                        }}
                      >
                        {t('vendor.vendorLogo')}
                      </Typography>
                      <Divider sx={{ 
                        mb: 3,
                        borderColor: isDarkMode ? '#404040' : 'inherit'
                      }} />
                      
                      {vendorInfo.logoUrl ? (
                        <ImagePreview>
                          <Avatar 
                            src={vendorInfo.logoUrl}
                            alt={vendorInfo.name || "Vendor"}
                            sx={{ 
                              width: 150, 
                              height: 150,
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                              border: '4px solid #fff',
                              backgroundColor: '#000000',
                              color: 'white',
                              fontSize: '3rem'
                            }}
                          >
                            {vendorInfo.name ? vendorInfo.name.charAt(0).toUpperCase() : 'V'}
                          </Avatar>
                          <Stack direction="row" spacing={2}>
                            <Button
                              variant="outlined"
                              component="label"
                              startIcon={<Upload size={16} />}
                              sx={{ 
                                borderRadius: '10px',
                                borderColor: isDarkMode ? '#404040' : '#667eea',
                                color: isDarkMode ? '#ffffff' : '#667eea',
                                '&:hover': {
                                  borderColor: '#667eea',
                                  backgroundColor: isDarkMode 
                                    ? 'rgba(102, 126, 234, 0.1)' 
                                    : 'rgba(102, 126, 234, 0.05)',
                                }
                              }}
                            >
                              {t('vendor.changeLogo')}
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleLogoUpload}
                              />
                            </Button>
                            <Button
                              variant="outlined"
                              color="secondary"
                              startIcon={<Visibility size={16} />}
                              onClick={handleViewLogo}
                              sx={{ 
                                borderRadius: '10px',
                                borderColor: isDarkMode ? '#404040' : '#667eea',
                                color: isDarkMode ? '#ffffff' : '#667eea',
                                '&:hover': {
                                  borderColor: '#667eea',
                                  backgroundColor: isDarkMode 
                                    ? 'rgba(102, 126, 234, 0.1)' 
                                    : 'rgba(102, 126, 234, 0.05)',
                                }
                              }}
                            >
                              {t('vendor.viewLogo')}
                            </Button>
                          </Stack>
                        </ImagePreview>
                      ) : (
                        <Box component="label" sx={{ cursor: 'pointer' }}>
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleLogoUpload}
                          />
                          <UploadBox>
                            <ImageIcon style={{ fontSize: 48, marginBottom: '16px', opacity: 0.7, color: '#667eea' }} />
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                mb: 1, 
                                fontWeight: 500, 
                                color: '#667eea' 
                              }}
                            >
                              {t('vendor.uploadLogo')}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: isDarkMode ? '#b3b3b3' : 'text.secondary' 
                              }}
                            >
                              {t('vendor.logoUploadText')}
                            </Typography>
                          </UploadBox>
                        </Box>
                      )}
                    </Grid>
                    
                    {/* Vendor Information */}
                    <Grid item xs={12} md={6}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mb: 2, 
                          fontWeight: 600,
                          color: isDarkMode ? '#ffffff' : 'inherit'
                        }}
                      >
                        {t('vendor.businessDetails')}
                      </Typography>
                      <Divider sx={{ 
                        mb: 3,
                        borderColor: isDarkMode ? '#404040' : 'inherit'
                      }} />
                      
                      <StyledTextField
                        label={t('vendor.businessName')}
                        fullWidth
                        value={vendorInfo.name}
                        onChange={(e) => setVendorInfo({...vendorInfo, name: e.target.value})}
                        required
                        variant="outlined"
                        placeholder={t('vendor.businessNamePlaceholder')}
                        helperText={t('vendor.businessNameHelper')}
                        sx={{ mb: 3 }}
                      />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <AnimatedButton 
                          onClick={handleSaveVendorInfo}
                          startIcon={<Save size={18} />}
                          disabled={loading}
                        >
                          {loading ? t('vendor.saving') : t('vendor.saveChanges')}
                        </AnimatedButton>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      )}
      
      {/* Branch Management Tab */}
      {currentTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StyledCard>
              <CardHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Store size={24} />
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                    {t('vendor.branchManagement')}
                  </Typography>
                </Box>
                <AnimatedButton
                  startIcon={<PlusCircle size={20} />}
                  onClick={handleAddBranch}
                >
                  {t('vendor.addNewBranch')}
                </AnimatedButton>
              </CardHeader>
              
              <CardContent sx={{ p: 0 }}>
                {/* Branches Table */}
                <Box sx={{ px: 3, pb: 3, pt: 3 }}>
                  {loadingBranches ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <StyledTableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell width="30%">{t('vendor.table.branchName')}</TableCell>
                            <TableCell width="35%">{t('vendor.table.address')}</TableCell>
                            <TableCell width="20%">{t('vendor.table.contactNumber')}</TableCell>
                            <TableCell width="15%" align="right">{t('vendor.table.actions')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {branches.length > 0 ? (
                            branches.map((branch) => (
                              <TableRow key={branch.branch_id || `branch-${Date.now()}-${Math.random()}`}>
                                {/* Table content */}
                                <TableCell>
                                  <Typography variant="body1" sx={{ 
                                    fontWeight: 500, 
                                    color: isDarkMode ? '#ffffff' : '#333' 
                                  }}>
                                    {branch.location_name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <MapPin size={16} color="#667eea" />
                                    <Typography variant="body2" sx={{ 
                                      color: isDarkMode ? '#b3b3b3' : '#4b5563' 
                                    }}>
                                      {branch.address || 'N/A'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Phone size={16} color="#667eea" />
                                    <Typography variant="body2" sx={{ 
                                      color: isDarkMode ? '#b3b3b3' : '#4b5563' 
                                    }}>
                                      {branch.contact_number || 'N/A'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <EditButton 
                                      size="small" 
                                      onClick={() => handleEditBranch(branch)}
                                    >
                                      <Edit2 size={16} />
                                    </EditButton>
                                    <DeleteButton 
                                      size="small" 
                                      onClick={() => handleDeleteBranch(branch)}
                                    >
                                      <Trash2 size={16} />
                                    </DeleteButton>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow key="no-branches">
                              <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                  <Store size={40} color="#d1d5db" />
                                  <Typography 
                                    variant="body1" 
                                    sx={{ 
                                      color: isDarkMode ? '#b3b3b3' : 'text.secondary' 
                                    }}
                                  >
                                    {t('vendor.noBranchesFound')}
                                  </Typography>
                                  <Button 
                                    variant="outlined" 
                                    startIcon={<PlusCircle size={16} />}
                                    onClick={handleAddBranch}
                                    sx={{ 
                                      mt: 1, 
                                      borderRadius: '10px',
                                      borderColor: isDarkMode ? '#404040' : '#667eea',
                                      color: isDarkMode ? '#ffffff' : '#667eea',
                                      '&:hover': {
                                        borderColor: '#667eea',
                                        backgroundColor: isDarkMode 
                                          ? 'rgba(102, 126, 234, 0.1)' 
                                          : 'rgba(102, 126, 234, 0.05)',
                                      }
                                    }}
                                  >
                                    {t('vendor.addBranch')}
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </StyledTableContainer>
                  )}
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      )}
      
      {/* Add Branch Dialog */}
      <AddNewBranch 
        open={addBranchDialogOpen}
        onClose={() => setAddBranchDialogOpen(false)}
        onBranchAdded={handleBranchAdded}
      />
      
      {/* Edit Branch Dialog */}
      {selectedBranch && (
        <EditBranch
          open={editBranchDialogOpen}
          onClose={() => setEditBranchDialogOpen(false)}
          branch={selectedBranch}
          onBranchUpdated={handleBranchUpdated}
        />
      )}
      
      {/* Delete Confirmation Dialog */}
      {selectedBranch && (
        <DeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleBranchDeleted}
          title={t('vendor.deleteBranch.title')}
          message={t('vendor.deleteBranch.message', { branchName: selectedBranch.location_name })}
        />
      )}
      
      {/* Success Dialog */}
      <SuccessDialog 
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
        message={successMessage}
      />
    </StyledContainer>
  );
};

export default VendorSetting;